// src/components/dashboard/PADDStaffDashboard.jsx
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847',
  cream: '#F0EFEB', warm: '#F7F6F3', sand: '#E2E0DB', white: '#FFFFFF',
  green: '#2E7D32', greenBg: '#e8f5e9', red: '#c0392b', redBg: '#fde8e8',
  blue: '#1565C0', blueBg: '#e3f2fd', amber: '#F57F17', amberBg: '#fff8e1',
};

const APP_STATUS_CONFIG = {
  draft:              { label: 'Draft',               color: '#6b7280', bg: '#f3f4f6' },
  questionnaire_done: { label: 'Questionnaire Done',  color: '#92400e', bg: '#fef3c7' },
  in_progress:        { label: 'In Progress',         color: '#065f46', bg: '#d1fae5' },
  under_review:       { label: 'Under Review',        color: '#1e40af', bg: '#dbeafe' },
  revision_needed:    { label: 'Revision Needed',     color: '#9a3412', bg: '#fee2e2' },
  submitted_to_ded:   { label: 'Submitted to DED',    color: '#1e40af', bg: '#dbeafe' },
  approved:           { label: 'Approved',            color: '#065f46', bg: '#d1fae5' },
  denied:             { label: 'Denied',              color: '#991b1b', bg: '#fee2e2' },
  withdrawn:          { label: 'Withdrawn',           color: '#6b7280', bg: '#f3f4f6' },
  completed:          { label: 'Completed',           color: '#065f46', bg: '#d1fae5' },
};

const FILTER_TABS = [
  { key: 'all',              label: 'All' },
  { key: 'under_review',     label: 'Under Review' },
  { key: 'revision_needed',  label: 'Revision Needed' },
  { key: 'in_progress',      label: 'In Progress' },
  { key: 'submitted_to_ded', label: 'Submitted to DED' },
  { key: 'draft',            label: 'Draft' },
];

export default function PADDStaffDashboard() {
  const { profile } = useAuth();
  const [applications, setApplications] = useState([]);
  const [docStats, setDocStats]         = useState({});   // { [appId]: { total, accepted, uploaded, pending } }
  const [noteCounts, setNoteCounts]     = useState({});   // { [appId]: count }
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState('all');
  const [savingId, setSavingId]         = useState(null); // appId whose status is being saved
  const [expandedNote, setExpandedNote] = useState(null); // appId whose note box is open
  const [noteText, setNoteText]         = useState('');
  const [savingNote, setSavingNote]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 1 — applications
      const { data: apps, error } = await supabase
        .from('applications')
        .select('id, project_name, status, created_at, municipalities(name, county)')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const ids = (apps || []).map(a => a.id);

      // 2 — document stats for all apps in one query
      const { data: docs } = ids.length
        ? await supabase
            .from('application_documents')
            .select('application_id, status')
            .in('application_id', ids)
        : { data: [] };

      const stats = {};
      for (const d of docs || []) {
        if (!stats[d.application_id]) stats[d.application_id] = { total: 0, accepted: 0, uploaded: 0, pending: 0, rejected: 0 };
        if (d.status !== 'not_applicable') stats[d.application_id].total++;
        if (d.status === 'accepted')  stats[d.application_id].accepted++;
        if (d.status === 'uploaded')  stats[d.application_id].uploaded++;
        if (d.status === 'pending')   stats[d.application_id].pending++;
        if (d.status === 'rejected')  stats[d.application_id].rejected++;
      }

      // 3 — note counts for all apps in one query
      const { data: notes } = ids.length
        ? await supabase
            .from('staff_notes')
            .select('application_id')
            .in('application_id', ids)
        : { data: [] };

      const counts = {};
      for (const n of notes || []) {
        counts[n.application_id] = (counts[n.application_id] || 0) + 1;
      }

      setApplications(apps || []);
      setDocStats(stats);
      setNoteCounts(counts);
    } catch (err) {
      console.error('Error loading dashboard:', err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(appId, newStatus) {
    setSavingId(appId);
    try {
      const updates = { status: newStatus };
      if (newStatus === 'submitted_to_ded') updates.submitted_at = new Date().toISOString();
      if (newStatus === 'approved')         updates.approved_at  = new Date().toISOString();
      await supabase.from('applications').update(updates).eq('id', appId);
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
    } catch (err) {
      console.error('Status update error:', err.message);
    }
    setSavingId(null);
  }

  async function submitNote(appId) {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await supabase.from('staff_notes').insert({
        application_id: appId,
        author_id: profile?.id,
        note_text: noteText.trim(),
        note_type: 'general',
        is_visible_to_clerk: true,
      });
      setNoteCounts(prev => ({ ...prev, [appId]: (prev[appId] || 0) + 1 }));
      setNoteText('');
      setExpandedNote(null);
    } catch (err) {
      console.error('Note error:', err.message);
    }
    setSavingNote(false);
  }

  const filtered = filter === 'all'
    ? applications
    : applications.filter(a => a.status === filter);

  // Summary counts
  const summary = {
    total:          applications.length,
    underReview:    applications.filter(a => a.status === 'under_review').length,
    revisionNeeded: applications.filter(a => a.status === 'revision_needed').length,
    docsUploaded:   Object.values(docStats).reduce((s, d) => s + d.uploaded, 0),
  };

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: C.slate, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      Loading applications…
    </div>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: 960, margin: '0 auto', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── SUMMARY STATS ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Applications',  value: summary.total,          color: C.navy,  bg: C.cream   },
          { label: 'Under Review',        value: summary.underReview,    color: '#1e40af', bg: '#dbeafe' },
          { label: 'Revision Needed',     value: summary.revisionNeeded, color: '#9a3412', bg: '#fee2e2' },
          { label: 'Docs Awaiting Review',value: summary.docsUploaded,   color: C.blue,  bg: C.blueBg  },
        ].map(({ label, value, color, bg }) => (
          <div key={label} style={{
            flex: '1 1 140px', background: C.white, borderRadius: 10,
            border: `1px solid ${C.sand}`, padding: '14px 18px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 28, fontWeight: 700, color, marginBottom: 2 }}>{value}</div>
            <div style={{ fontSize: 11, color: C.slate, fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── FILTER TABS ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {FILTER_TABS.map(({ key, label }) => {
          const count = key === 'all' ? applications.length : applications.filter(a => a.status === key).length;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                fontWeight: filter === key ? 700 : 400,
                background: filter === key ? C.navy : C.white,
                color: filter === key ? C.cream : C.slate,
                border: `1px solid ${filter === key ? C.navy : C.sand}`,
                transition: 'all 0.15s',
              }}
            >
              {label}
              {count > 0 && (
                <span style={{
                  marginLeft: 6, padding: '1px 7px', borderRadius: 10, fontSize: 11,
                  background: filter === key ? 'rgba(255,255,255,0.2)' : C.sand,
                  color: filter === key ? C.cream : C.slate,
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── APPLICATION CARDS ── */}
      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: C.slate, fontSize: 14, background: C.white, borderRadius: 10, border: `1px solid ${C.sand}` }}>
          No applications match this filter.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.map(app => {
          const sc    = APP_STATUS_CONFIG[app.status] || APP_STATUS_CONFIG.draft;
          const ds    = docStats[app.id]    || { total: 0, accepted: 0, uploaded: 0, pending: 0, rejected: 0 };
          const nc    = noteCounts[app.id]  || 0;
          const pct   = ds.total > 0 ? Math.round((ds.accepted / ds.total) * 100) : 0;
          const isSaving = savingId === app.id;
          const noteOpen  = expandedNote === app.id;

          return (
            <div key={app.id} style={{
              background: C.white, borderRadius: 12, border: `1px solid ${C.sand}`,
              overflow: 'hidden',
              boxShadow: noteOpen ? `0 0 0 2px ${C.gold}` : 'none',
              transition: 'box-shadow 0.15s',
            }}>
              {/* ── CARD MAIN ROW ── */}
              <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>

                {/* Project info */}
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 2 }}>
                    {app.project_name || 'Untitled Application'}
                  </div>
                  {app.municipalities && (
                    <div style={{ fontSize: 12, color: C.slate }}>
                      {app.municipalities.name}
                      {app.municipalities.county ? ` — ${app.municipalities.county} County` : ''}
                    </div>
                  )}
                </div>

                {/* Doc progress */}
                {ds.total > 0 && (
                  <div style={{ minWidth: 120, flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: C.slate, marginBottom: 4, fontWeight: 600 }}>
                      Documents: {ds.accepted}/{ds.total} accepted
                      {ds.uploaded > 0 && <span style={{ color: C.blue, marginLeft: 6 }}>{ds.uploaded} awaiting review</span>}
                      {ds.rejected > 0 && <span style={{ color: C.red, marginLeft: 6 }}>{ds.rejected} needs resubmission</span>}
                    </div>
                    <div style={{ height: 6, background: C.sand, borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 4,
                        width: `${pct}%`,
                        background: pct === 100 ? C.green : C.gold,
                        transition: 'width 0.4s',
                      }} />
                    </div>
                  </div>
                )}

                {/* Status dropdown */}
                <select
                  value={app.status}
                  disabled={isSaving}
                  onChange={(e) => updateStatus(app.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    color: sc.color, background: sc.bg,
                    border: `1.5px solid ${sc.color}`,
                    cursor: isSaving ? 'wait' : 'pointer',
                    opacity: isSaving ? 0.6 : 1,
                    flexShrink: 0,
                  }}
                >
                  {Object.entries(APP_STATUS_CONFIG).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </select>

                {/* Note toggle button */}
                <button
                  onClick={() => {
                    setExpandedNote(noteOpen ? null : app.id);
                    setNoteText('');
                  }}
                  title="Add a quick note"
                  style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: noteOpen ? C.gold : C.cream,
                    color: noteOpen ? '#fff' : C.slate,
                    border: `1px solid ${noteOpen ? C.gold : C.sand}`,
                    cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
                  }}
                >
                  💬
                  {nc > 0 && (
                    <span style={{
                      background: noteOpen ? 'rgba(255,255,255,0.3)' : C.sand,
                      color: noteOpen ? '#fff' : C.slate,
                      borderRadius: 10, padding: '0 5px', fontSize: 11,
                    }}>
                      {nc}
                    </span>
                  )}
                </button>

                {/* Open detail link */}
                <Link
                  to={`/apply/${app.id}`}
                  style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: C.navy, color: C.cream, textDecoration: 'none',
                    flexShrink: 0,
                  }}
                >
                  Open →
                </Link>
              </div>

              {/* ── QUICK NOTE EXPANDER ── */}
              {noteOpen && (
                <div style={{
                  borderTop: `1px solid ${C.sand}`,
                  padding: '14px 18px',
                  background: C.warm,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.slate, marginBottom: 8 }}>
                    Add a note (visible to municipality):
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Type your note…"
                      rows={2}
                      autoFocus
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 8,
                        border: `1.5px solid ${C.sand}`, fontSize: 13,
                        fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                        color: C.navy,
                      }}
                      onFocus={(e) => (e.target.style.borderColor = C.gold)}
                      onBlur={(e)  => (e.target.style.borderColor = C.sand)}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button
                        onClick={() => submitNote(app.id)}
                        disabled={savingNote || !noteText.trim()}
                        style={{
                          padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                          background: savingNote || !noteText.trim() ? C.sand : C.gold,
                          color: savingNote || !noteText.trim() ? C.slate : '#fff',
                          border: 'none',
                          cursor: savingNote || !noteText.trim() ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {savingNote ? 'Saving…' : 'Save Note'}
                      </button>
                      <button
                        onClick={() => { setExpandedNote(null); setNoteText(''); }}
                        style={{
                          padding: '6px 18px', borderRadius: 8, fontSize: 12,
                          background: 'transparent', color: C.slate,
                          border: `1px solid ${C.sand}`, cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
